import { useGitHubReposList } from "@/components/EntitySelector/integrations/GitHubSelector";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIntegrationConnection } from "@/hooks/useIntegrationConnection";
import { GithubRepoEntity } from "@cohesive/api-entities";
import { getEntityId, Spinner } from "@cohesive/ui-components";
import { ControlProps, optionIs, rankWith } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { ChevronsUpDown, FolderGit2, X } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";

interface GitHubRepoControlProps extends ControlProps {
  data: GithubRepoEntity | string | undefined;
}

const GitHubRepoControlComponent: React.FC<GitHubRepoControlProps> = ({
  data,
  enabled,
  handleChange,
  path,
  label,
  visible,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const integration = useIntegrationConnection("github");

  // Extract ID from data (handles both string ID and entity object)
  // Handle both string IDs and entity objects
  const selectedId =
    typeof data === "string" ? data : data ? getEntityId(data as any) : null;

  const {
    data: reposResponse,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGitHubReposList(integration!);

  const repos: GithubRepoEntity[] = reposResponse?.data || [];

  // Automatically fetch all pages when dropdown is opened
  useEffect(() => {
    if (open && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [open, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const filterFn = (entity: GithubRepoEntity) => {
    const repo = entity.data;
    const { full_name, owner, description = "" } = repo;
    const query = searchQuery.toLowerCase();
    return (
      full_name.toLowerCase().includes(query) ||
      owner.toLowerCase().includes(query) ||
      description.toLowerCase().includes(query)
    );
  };

  const renderRepoIcon = () => {
    return (
      <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-muted">
        <FolderGit2 className="w-4! h-4!" />
      </div>
    );
  };

  const selectedRepo = useMemo(
    () => repos.find((repo) => selectedId === repo.data.id),
    [repos, selectedId],
  );

  if (!visible) return null;

  return (
    <div className="w-full">
      {label && <label className="text-sm font-medium">{label}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            disabled={!enabled}
          >
            {selectedRepo ? (
              <div className="flex items-center gap-2 overflow-hidden">
                {renderRepoIcon()}
                <span className="truncate">{selectedRepo.data.full_name}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">
                Select repository...
              </span>
            )}
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {selectedRepo && (
                <div
                  role="button"
                  aria-label="Clear selection"
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleChange(path, null);
                  }}
                >
                  <X className="h-4 w-4 opacity-50 hover:opacity-100" />
                </div>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search repositories..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {isLoading && repos.length === 0 ? (
                <div className="flex items-center justify-center p-4">
                  <Spinner size="sm" />
                </div>
              ) : (
                <>
                  <CommandEmpty>No repositories found.</CommandEmpty>

                  {/* All Repositories */}
                  {repos.filter(filterFn).length > 0 && (
                    <CommandGroup heading="Repositories">
                      {repos
                        .filter(filterFn)
                        .sort((a, b) =>
                          a.data.full_name.localeCompare(b.data.full_name),
                        )
                        .map((entity) => {
                          return (
                            <CommandItem
                              key={entity.data.id}
                              value={entity.data.full_name}
                              onSelect={() => {
                                // Return full entity object
                                handleChange(path, entity);
                                setOpen(false);
                              }}
                            >
                              {renderRepoIcon()}
                              <span className="truncate">
                                {entity.data.full_name}
                              </span>
                            </CommandItem>
                          );
                        })}
                    </CommandGroup>
                  )}

                  {/* Loading more indicator */}
                  {isFetchingNextPage && (
                    <div className="flex items-center justify-center p-2 text-xs text-muted-foreground">
                      <Spinner size="sm" className="mr-2" />
                      Loading more repositories...
                    </div>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const GitHubRepoControlTester = rankWith(
  10,
  optionIs("control", "github_repository"),
);

export const GitHubRepoControl = withJsonFormsControlProps(
  GitHubRepoControlComponent,
);
